import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  ChevronRight,
  Download,
  History,
  Loader2,
  PanelRightClose,
  PanelRightOpen,
} from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button, buttonVariants } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover"
import { UnifiedAiPanel } from "@/components/drafting/UnifiedAiPanel"
import { ResizeHandle } from "@/components/ui/ResizeHandle"
import { useElementWidth } from "@/hooks/useElementWidth"
import { useBidMembers } from "@/hooks/useBidMembers"
import { useScheduleSeedTurn } from "@/hooks/useScheduleSeedTurn"
import { useAgentStore } from "@/stores/agent-store"
import { useScheduleStore } from "@/stores/schedule-store"
import { ProposalPopover, SourcePill } from "@/components/schedule/ProposalPopover"
import {
  FILL_SOURCE_TIER_LABEL,
  NEEDS_VALUE_HINT,
  cellTargetOf,
  effectiveFillValue,
  isOnSheet,
  needsUserValue,
} from "@/lib/fill-source"
import { WorkbookGrid, type WorkbookGridHandle } from "@/components/schedule/WorkbookGrid"
import { formatCellValue } from "@/lib/xlsx-format"
import { previewFormulaValues } from "@/lib/formula-preview"
import {
  DEFAULT_AI_PANEL_WIDTH,
  MIN_AI_PANEL_WIDTH,
  maxAiPanelWidthFor,
  resolvePaneWidths,
} from "@/lib/pane-layout"
import { cn } from "@/lib/utils"
import { DOCUMENT_SECTION_KEY } from "@/types/scope"
import type { ScheduleExportResult } from "@/lib/api"
import type {
  FillProposal,
  FillSource,
  ScanResult,
  ScheduleIngestStatus,
  SheetModel,
  WorkbookModel,
} from "@/types/schedule"

// ===================
// Copy
// ===================

const INGEST_PILL: Record<
  ScheduleIngestStatus,
  { label: string; variant: "warning" | "success" | "destructive" }
> = {
  pending: { label: "Preparing", variant: "warning" },
  ready: { label: "Ready", variant: "success" },
  failed: { label: "Couldn't prepare", variant: "destructive" },
}

const RECALC_NOTICE: Partial<Record<ScanResult["status"], string>> = {
  recalc_failed:
    "Formula totals couldn't be revalidated — open the file in Excel to confirm them.",
  recalc_skipped:
    "This workbook links to other files, so formulas weren't revalidated — open it in Excel to refresh them.",
}

const MACROS_NOTICE = "Macros were removed — the buyer receives a standard .xlsx file."

/** Matches the editors' handle: ResizeHandle renders `w-1`. */
const RESIZE_HANDLE_WIDTH = 4

function plural(count: number, singular: string, pluralForm: string): string {
  return count === 1 ? singular : pluralForm
}

function formatTimestamp(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return "—"
  return date.toLocaleString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

// ===================
// Helpers
// ===================

/** The formatted value that sits in the workbook today, for a proposal's cell. */
function currentDisplayFor(model: WorkbookModel | null, proposal: FillProposal): string {
  const target = cellTargetOf(proposal)
  const sheet = model?.sheets.find((s) => s.name === target?.sheet)
  const cell = sheet?.rows
    .flatMap((row) => row.cells)
    .find((c) => c.address === target?.address)
  const display = cell ? formatCellValue(cell.value, cell.numFmt) : ""
  if (display) return display
  return proposal.currentValue ?? "—"
}

function pendingBySheet(proposals: FillProposal[]): Map<string, number> {
  const counts = new Map<string, number>()
  for (const proposal of proposals) {
    const target = cellTargetOf(proposal)
    if (proposal.status !== "pending" || !target) continue
    counts.set(target.sheet, (counts.get(target.sheet) ?? 0) + 1)
  }
  return counts
}

/**
 * Pending counts per source tier **for one sheet**, minus `needs_input` — those
 * carry no value worth writing, so bulk-accepting them would fill cells with
 * placeholders.
 *
 * Sheet-scoped on purpose: these buttons sit beside the sheet tabs and next to
 * a sheet-scoped "Accept all", so a workbook-wide sweep here would be a
 * surprise the user only discovers after exporting. Workbook-wide bulk belongs
 * in the assistant panel, where it can say so.
 */
function pendingByTier(
  proposals: FillProposal[],
  sheetName: string
): Array<{ source: FillSource; count: number }> {
  const counts = new Map<FillSource, number>()
  for (const proposal of proposals) {
    if (proposal.status !== "pending") continue
    if (!isOnSheet(proposal, sheetName)) continue
    if (proposal.source === "needs_input") continue
    counts.set(proposal.source, (counts.get(proposal.source) ?? 0) + 1)
  }
  return Array.from(counts.entries())
    .map(([source, count]) => ({ source, count }))
    .sort((a, b) => b.count - a.count)
}

// ===================
// Page
// ===================

export function ExcelSchedulePage() {
  const { bidId, artifactId } = useParams<{ bidId: string; artifactId: string }>()
  const navigate = useNavigate()

  const {
    model,
    schedule,
    proposals,
    counts,
    activeSheet,
    exports,
    isLoading,
    isExporting,
    error,
    load,
    refreshExports,
    setActiveSheet,
    createManualFill,
    decide,
    decideAll,
    doExport,
    reingest,
    reset,
  } = useScheduleStore()

  const gridRef = useRef<HTMLDivElement>(null)
  const gridApiRef = useRef<WorkbookGridHandle>(null)
  /** Whatever opened the popover, so focus can go back there on close. */
  const triggerRef = useRef<HTMLElement | null>(null)
  const [selected, setSelected] = useState<{
    address: string
    /** Null until the cell has been located in the DOM (it may need scrolling to). */
    rect: { top: number; left: number; width: number; height: number } | null
  } | null>(null)
  const [exportResult, setExportResult] = useState<ScheduleExportResult | null>(null)
  const [historyOpen, setHistoryOpen] = useState(false)
  // Collapsed by default: the grid already shows the fills. Opens on demand, and
  // for a tail longer than one screen of rows (see the list's own comment).
  const [listOpen, setListOpen] = useState(false)

  // Resizable/collapsible AI panel, matching the section and document editors —
  // a workbook is the widest content in the app, so reclaiming the pane for the
  // grid (or widening it for the agent's tables) matters more here than anywhere.
  // Width shares the editors' `ai-panel-width` key so the pane keeps its size
  // across surfaces.
  const [aiPanelWidth, setAiPanelWidth] = useState(() => {
    const saved = localStorage.getItem("ai-panel-width")
    return saved ? parseInt(saved, 10) : DEFAULT_AI_PANEL_WIDTH
  })
  const [isResizing, setIsResizing] = useState(false)
  const {
    ref: paneContainerRef,
    element: paneContainerEl,
    width: paneContainerWidth,
  } = useElementWidth()

  // Bid members — the panel's Comments tab needs them for @mention
  // autocomplete and commenter avatars, same as the editor pages.
  const bidMembers = useBidMembers(bidId)
  const resetAgentStore = useAgentStore((s) => s.reset)
  const isPanelOpen = useAgentStore((s) => s.isPanelOpen)
  const togglePanel = useAgentStore((s) => s.togglePanel)

  useEffect(() => {
    if (!artifactId) return
    void load(artifactId)
    return () => reset()
  }, [artifactId, load, reset])

  // Leaving the workbook (or switching to another one) must not carry this
  // fill thread's session into the next surface — same rule the editor pages
  // apply.
  useEffect(() => {
    return () => resetAgentStore()
  }, [artifactId, resetAgentStore])

  useEffect(() => {
    localStorage.setItem("ai-panel-width", String(aiPanelWidth))
  }, [aiPanelWidth])

  useEffect(() => {
    if (!isResizing) return

    const handleMouseMove = (e: MouseEvent) => {
      if (!paneContainerEl) return
      const rect = paneContainerEl.getBoundingClientRect()
      // Cap the drag so the grid keeps a usable minimum width.
      const max = maxAiPanelWidthFor(rect.width, 0, RESIZE_HANDLE_WIDTH)
      setAiPanelWidth(Math.max(MIN_AI_PANEL_WIDTH, Math.min(max, rect.right - e.clientX)))
    }
    const handleMouseUp = () => setIsResizing(false)

    document.addEventListener("mousemove", handleMouseMove)
    document.addEventListener("mouseup", handleMouseUp)
    document.body.style.cursor = "col-resize"
    document.body.style.userSelect = "none"
    return () => {
      document.removeEventListener("mousemove", handleMouseMove)
      document.removeEventListener("mouseup", handleMouseUp)
      document.body.style.cursor = ""
      document.body.style.userSelect = ""
    }
  }, [isResizing, paneContainerEl])

  // Resolved here rather than at render so the drag cap sees the width actually
  // on screen (a stored width wider than this viewport must not squeeze the grid).
  const { aiPanelWidth: renderedAiPanelWidth } = resolvePaneWidths({
    containerWidth: paneContainerWidth,
    outlineWidth: null,
    aiPanelWidth: isPanelOpen ? aiPanelWidth : null,
    handlesWidth: isPanelOpen ? RESIZE_HANDLE_WIDTH : 0,
  })
  const effectiveAiPanelWidth = renderedAiPanelWidth ?? aiPanelWidth

  // Armed by the AI panel below: its `initSession` commits the artifact-scoped
  // session every guard in here waits for.
  useScheduleSeedTurn(artifactId ?? null)

  const backToResponses = () => void navigate(`/app/bids/${bidId}/write`)

  const sheet: SheetModel | null = useMemo(() => {
    if (!model) return null
    return model.sheets.find((s) => s.name === activeSheet) ?? model.sheets[0] ?? null
  }, [model, activeSheet])

  const sheetPending = useMemo(() => pendingBySheet(proposals), [proposals])
  const tiers = useMemo(
    () => pendingByTier(proposals, sheet?.name ?? ""),
    [proposals, sheet?.name]
  )
  const sheetPendingList = useMemo(
    () =>
      proposals.filter((p) => p.status === "pending" && isOnSheet(p, sheet?.name ?? "")),
    [proposals, sheet?.name]
  )
  /**
   * What "Accept all on this sheet" would actually decide. `needs_input` fills
   * carry no value, so the backend's bulk accept skips them — the count has to
   * skip them too, or the button promises writes it will not make (and reads as
   * disagreeing with the per-tier buttons, which already exclude the tier).
   */
  const sheetAcceptableCount = useMemo(
    () => sheetPendingList.filter((p) => !needsUserValue(p)).length,
    [sheetPendingList]
  )

  /**
   * What each calculated cell will hold. A value verified by LibreOffice at the
   * last export always wins over the live preview — it is the number the buyer
   * will actually see. Cells we cannot evaluate honestly are simply absent, and
   * render as "calculates in Excel" rather than a guess.
   */
  const calculated = useMemo(() => {
    if (!model || !sheet) return new Map<string, { value: number; verified: boolean }>()

    const merged = new Map<string, { value: number; verified: boolean }>()
    for (const [address, value] of previewFormulaValues(model, sheet.name, proposals)) {
      merged.set(address, { value, verified: false })
    }
    for (const [key, value] of Object.entries(schedule?.computedValues ?? {})) {
      const [sheetName, address] = key.split("!")
      if (sheetName !== sheet.name || !address || typeof value !== "number") continue
      merged.set(address, { value, verified: true })
    }
    return merged
  }, [model, sheet, proposals, schedule?.computedValues])

  const selectedProposal = useMemo(() => {
    if (!selected || !sheet) return null
    // A re-staged cell keeps its earlier rows as `superseded`. Matching on address
    // alone opened the popover on the dead one — showing "Needs your input" and a
    // disabled Accept over a cell the grid was already painting with a good value.
    // Mirror the grid: ignore superseded, and prefer the actionable pending row.
    const candidates = proposals.filter(
      (p) =>
        isOnSheet(p, sheet.name) &&
        cellTargetOf(p)?.address === selected.address &&
        p.status !== 'superseded'
    )
    return candidates.find((p) => p.status === 'pending') ?? candidates[0] ?? null
  }, [selected, proposals, sheet])

  const cellElement = (address: string) =>
    gridRef.current?.querySelector<HTMLElement>(`[data-cell-address="${address}"]`) ?? null

  /**
   * Open the review popover for a cell. A cell reached from the review list can
   * be far outside the rendered window, so scroll it in first — the popover is
   * measured against the real cell on the commit that follows, never against a
   * missing one.
   */
  const openCell = (address: string) => {
    triggerRef.current = document.activeElement as HTMLElement | null
    if (!cellElement(address)) gridApiRef.current?.scrollToAddress(address)
    setSelected({ address, rect: null })
  }

  // Measure after the grid has committed the (possibly newly scrolled) window.
  useLayoutEffect(() => {
    if (!selected || selected.rect) return
    const rect = cellElement(selected.address)?.getBoundingClientRect()
    setSelected((prev) =>
      prev && prev.address === selected.address
        ? {
            ...prev,
            rect: rect
              ? { top: rect.top, left: rect.left, width: rect.width, height: rect.height }
              : { top: 0, left: 0, width: 0, height: 0 },
          }
        : prev
    )
  }, [selected])

  /** Escape / outside-click must land back on the control that opened this. */
  const restoreTriggerFocus = () => {
    const trigger = triggerRef.current
    if (trigger?.isConnected) {
      trigger.focus()
      return
    }
    // The trigger was re-rendered away (a decision moved the row) — fall back to
    // the cell itself so focus never drops to <body>.
    const fallback = selected ? cellElement(selected.address)?.querySelector("button") : null
    fallback?.focus()
  }

  const handleExport = async () => {
    const result = await doExport()
    if (!result) return
    setExportResult(result)
    // A workbook that came back with fresh Excel errors must be looked at, not
    // shipped — so only a clean scan opens the file straight away.
    if (result.scan.status !== "errors_found") {
      window.open(result.downloadUrl, "_blank", "noopener,noreferrer")
    }
  }

  const openHistory = () => {
    setHistoryOpen(true)
    void refreshExports()
  }

  // ── Ingest gates ──

  if (!schedule && isLoading) {
    return (
      <CentredState>
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </CentredState>
    )
  }

  if (schedule?.ingestStatus === "failed") {
    return (
      <CentredState>
        <AlertCircle className="h-8 w-8 text-destructive" />
        <p className="max-w-md text-sm text-muted-foreground">
          {schedule.ingestError ?? "We couldn't prepare this workbook."}
        </p>
        <div className="flex items-center gap-2">
          <Button onClick={() => void reingest()}>Try again</Button>
          <Button variant="ghost" onClick={backToResponses}>
            Back to Write
          </Button>
        </div>
      </CentredState>
    )
  }

  const ingestStatus = schedule?.ingestStatus ?? "pending"
  const pill = INGEST_PILL[ingestStatus]
  const isPreparing = ingestStatus === "pending" || (!model && !error)
  // The centred model-error state already states the problem — a banner above it
  // would say the same thing twice.
  const showErrorBanner = Boolean(error) && (isPreparing || Boolean(model))

  return (
    <div className="flex h-full flex-col">
      {/* Metadata strip */}
      <div className="sticky top-0 z-10 flex items-center justify-between gap-4 border-b border-border bg-background px-6 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={backToResponses}
            aria-label="Back"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="truncate text-sm font-medium">
            {schedule?.sourceFilename ?? "Schedule fill"}
          </h1>
          <Badge variant={pill.variant} className="shrink-0 whitespace-nowrap text-[10px]">
            {pill.label}
          </Badge>
          {counts && (
            <span className="hidden shrink-0 text-meta md:inline">
              {counts.pending} to review · {counts.accepted} accepted · {counts.applied} filled
            </span>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {/* A workbook is the widest content in the app — make reclaiming the
              pane (and getting it back) obvious rather than a hidden gesture. */}
          <Button
            variant="ghost"
            size="icon"
            className="hidden lg:inline-flex"
            onClick={togglePanel}
            aria-label={isPanelOpen ? "Hide AI fill assistant" : "Show AI fill assistant"}
            title={isPanelOpen ? "Hide AI fill assistant" : "Show AI fill assistant"}
          >
            {isPanelOpen ? (
              <PanelRightClose className="h-4 w-4" />
            ) : (
              <PanelRightOpen className="h-4 w-4" />
            )}
          </Button>
          <Button variant="ghost" size="sm" onClick={openHistory}>
            <History className="mr-1.5 h-4 w-4" />
            Export history
          </Button>
          <Button
            size="sm"
            onClick={() => void handleExport()}
            disabled={isExporting || (counts?.accepted ?? 0) === 0}
            title={
              (counts?.accepted ?? 0) === 0
                ? "Accept at least one fill before exporting"
                : undefined
            }
          >
            {isExporting ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-1.5 h-4 w-4" />
            )}
            Apply &amp; download
          </Button>
        </div>
      </div>

      {showErrorBanner && (
        <p className="flex items-center gap-2 border-b border-border bg-destructive/10 px-6 py-2 text-xs text-destructive">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          {error}
        </p>
      )}

      {isPreparing ? (
        <CentredState>
          <Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--ai-accent-text))]" />
          <p className="text-sm text-muted-foreground">Preparing your workbook…</p>
          <p className="max-w-sm text-meta">
            We're reading every sheet so the fill assistant knows where the answers go.
          </p>
        </CentredState>
      ) : !model ? (
        // Ingest succeeded but the presigned model fetch didn't — retryable, and
        // distinct from a workbook we could never read.
        <CentredState>
          <AlertCircle className="h-8 w-8 text-destructive" />
          <p className="text-sm text-muted-foreground">
            We couldn't open this workbook just now.
          </p>
          <Button
            variant="outline"
            onClick={() => artifactId && void load(artifactId)}
          >
            Try again
          </Button>
        </CentredState>
      ) : (
        <div className="flex min-h-0 flex-1" ref={paneContainerRef}>
          {/* Workspace */}
          <div className="flex min-w-0 flex-1 flex-col bg-card">
            {/* Sheet tabs */}
            {model && model.sheets.length > 0 && (
              <div className="flex items-center gap-2 overflow-x-auto px-6 py-3">
                <div
                  role="tablist"
                  aria-label="Sheets"
                  className="inline-flex h-9 items-center gap-1 rounded-lg bg-muted p-1"
                >
                  {model.sheets.map((s) => {
                    const isActive = s.name === sheet?.name
                    const pending = sheetPending.get(s.name) ?? 0
                    return (
                      <button
                        key={s.name}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        onClick={() => setActiveSheet(s.name)}
                        className={cn(
                          "flex h-7 items-center gap-1.5 whitespace-nowrap rounded-md px-3 text-sm transition-colors",
                          isActive
                            ? "bg-card font-medium shadow-sm"
                            : "text-muted-foreground hover:text-foreground"
                        )}
                      >
                        {s.name}
                        {pending > 0 && (
                          <span className="rounded-full bg-[hsl(var(--brand)/0.12)] px-1.5 text-[10px] text-[hsl(var(--ai-accent-text))]">
                            {pending}
                          </span>
                        )}
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Bulk actions */}
            {(sheetAcceptableCount > 0 || tiers.length > 0) && (
              <div className="flex flex-wrap items-center gap-1 px-6 pb-2">
                {sheetAcceptableCount > 0 && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      void decideAll("accepted", { sheet: sheet?.name ?? "" })
                    }
                  >
                    Accept all {sheetAcceptableCount} on this sheet
                  </Button>
                )}
                {tiers.map(({ source, count }) => (
                  <Button
                    key={source}
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      void decideAll("accepted", { sheet: sheet?.name ?? "", source })
                    }
                  >
                    Accept all {count} {FILL_SOURCE_TIER_LABEL[source]}{" "}
                    {plural(count, "fill", "fills")} on this sheet
                  </Button>
                ))}
              </div>
            )}

            {/* Grid */}
            <div ref={gridRef} className="min-h-0 flex-1 border-t border-border">
              {sheet ? (
                <WorkbookGrid
                  ref={gridApiRef}
                  sheet={sheet}
                  proposals={proposals}
                  onCellClick={openCell}
                  selectedAddress={selected?.address ?? null}
                  onCellEdit={async (address, value) => {
                    await createManualFill(sheet.name, address, value)
                  }}
                  calculated={calculated}
                />
              ) : (
                <p className="p-6 text-sm text-muted-foreground">
                  This workbook has no readable sheets.
                </p>
              )}
            </div>

            {/*
              Long-tail review list. The grid already shows every pending fill in
              place, so the list is redundant on a short sheet and was eating a
              fixed quarter of the workspace even when empty. It earns its keep at
              scale — 40 fills scattered down a sheet, and the only keyboard path
              through the tail — so it collapses instead of disappearing, and opens
              itself when the list outgrows what the grid can show at once.
            */}
            {sheetPendingList.length > 0 && (
              <div
                data-testid="pending-fill-list"
                className="shrink-0 border-t border-border px-6 py-3"
              >
                <button
                  type="button"
                  onClick={() => setListOpen((open) => !open)}
                  aria-expanded={listOpen}
                  className="flex w-full items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
                >
                  <ChevronRight
                    className={cn("h-3.5 w-3.5 transition-transform", listOpen && "rotate-90")}
                    aria-hidden="true"
                  />
                  {sheetPendingList.length}{" "}
                  {plural(sheetPendingList.length, "fill", "fills")} to review on this sheet
                </button>
                {listOpen && (
                  <div className="mt-2 max-h-56 divide-y divide-border overflow-auto">
                    {sheetPendingList.map((proposal) => (
                      <PendingFillRow
                        key={proposal.id}
                        proposal={proposal}
                        currentDisplay={currentDisplayFor(model, proposal)}
                        onShow={() => openCell(cellTargetOf(proposal)?.address ?? "")}
                        onDecide={(decision) => void decide(proposal.id, decision)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/*
            AI fill assistant. Mounting it is what arms the seed turn above:
            `initSession` commits the artifact-scoped `activeScope` the hook
            waits for. Keyed on the artifact so switching workbooks rebuilds the
            session instead of inheriting the previous one's thread.
          */}
          {isPanelOpen && (
            <ResizeHandle
              onMouseDown={(e) => {
                e.preventDefault()
                setIsResizing(true)
              }}
              isResizing={isResizing}
              aria-label="Resize AI panel"
              className="hidden lg:block"
            />
          )}

          {isPanelOpen && (
            <aside
              className="hidden shrink-0 flex-col overflow-hidden border-l border-border bg-muted/25 lg:flex"
              style={{ width: effectiveAiPanelWidth }}
              data-ai-pane="true"
            >
              <UnifiedAiPanel
                key={artifactId ?? ""}
                bidId={bidId ?? ""}
                artifactId={artifactId ?? ""}
                sectionKey={DOCUMENT_SECTION_KEY}
                scopeKind="artifact"
                scopeLabel="This schedule"
                bidMembers={bidMembers}
              />
            </aside>
          )}
        </div>
      )}

      {/* Cell popover */}
      <Popover
        open={selectedProposal !== null && selected?.rect != null}
        onOpenChange={(open) => {
          if (!open) setSelected(null)
        }}
      >
        <PopoverAnchor asChild>
          <div
            aria-hidden="true"
            className="pointer-events-none fixed"
            style={{
              top: selected?.rect?.top ?? 0,
              left: selected?.rect?.left ?? 0,
              width: selected?.rect?.width ?? 0,
              height: selected?.rect?.height ?? 0,
            }}
          />
        </PopoverAnchor>
        <PopoverContent
          align="start"
          className="w-80 p-3"
          onCloseAutoFocus={(event) => {
            event.preventDefault()
            restoreTriggerFocus()
          }}
        >
          {selectedProposal && (
            <ProposalPopover
              proposal={selectedProposal}
              currentDisplay={currentDisplayFor(model, selectedProposal)}
              onDecide={(decision, editedValue) => {
                void decide(selectedProposal.id, decision, editedValue)
                setSelected(null)
              }}
              onClose={() => setSelected(null)}
            />
          )}
        </PopoverContent>
      </Popover>

      <ExportResultDialog
        result={exportResult}
        isXlsm={schedule?.isXlsm ?? false}
        onClose={() => setExportResult(null)}
      />

      <ExportHistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        exports={exports}
      />
    </div>
  )
}

// ===================
// Sub-components
// ===================

function CentredState({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
      {children}
    </div>
  )
}

interface PendingFillRowProps {
  proposal: FillProposal
  currentDisplay: string
  onShow: () => void
  onDecide: (decision: "accepted" | "dismissed") => void
}

function PendingFillRow({ proposal, currentDisplay, onShow, onDecide }: PendingFillRowProps) {
  const isUnanswered = needsUserValue(proposal)
  return (
    <div className="flex items-center gap-3 py-1.5">
      <button
        type="button"
        onClick={onShow}
        className="shrink-0 font-mono text-meta hover:text-foreground"
        title="Show this cell in the grid"
      >
        {cellTargetOf(proposal)?.address}
      </button>
      <div className="flex min-w-0 flex-1 items-baseline gap-1.5 text-sm">
        <s className="shrink-0 text-meta">{currentDisplay}</s>
        <span className="truncate">{effectiveFillValue(proposal)}</span>
      </div>
      <SourcePill proposal={proposal} />
      {proposal.warning && (
        <AlertTriangle
          className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--status-warning))]"
          aria-label="Has a warning"
        />
      )}
      <div className="flex shrink-0 items-center gap-1">
        {/* Wrapper carries the hint: a disabled button is not a hover target. */}
        <span title={isUnanswered ? NEEDS_VALUE_HINT : undefined}>
          <Button
            variant="ghost"
            size="sm"
            disabled={isUnanswered}
            onClick={() => onDecide("accepted")}
          >
            Accept
          </Button>
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground"
          onClick={() => onDecide("dismissed")}
        >
          Dismiss
        </Button>
      </div>
    </div>
  )
}

interface ExportResultDialogProps {
  result: ScheduleExportResult | null
  isXlsm: boolean
  onClose: () => void
}

function ExportResultDialog({ result, isXlsm, onClose }: ExportResultDialogProps) {
  const errors = result?.scan.newErrors ?? []
  const hasErrors = result?.scan.status === "errors_found"
  const recalcNotice = result ? RECALC_NOTICE[result.scan.status] : undefined

  return (
    <Dialog open={result !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{hasErrors ? "Check this workbook first" : "Filled workbook ready"}</DialogTitle>
          <DialogDescription>
            {hasErrors
              ? "Your accepted fills were written, but some cells now show an Excel error."
              : "Your accepted fills were written into a copy of the original workbook."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {hasErrors && (
            <div className="space-y-1 rounded-lg bg-destructive/10 px-3 py-2">
              <p className="text-xs font-medium text-destructive">
                {errors.length} {plural(errors.length, "cell", "cells")} to check
              </p>
              <ul className="space-y-0.5">
                {errors.map((cellError) => (
                  <li
                    key={`${cellError.sheet}!${cellError.address}`}
                    className="font-mono text-xs text-destructive"
                  >
                    {cellError.sheet}!{cellError.address} {cellError.code}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(result?.pendingCount ?? 0) > 0 && (
            <Notice tone="warning">
              {result?.pendingCount} proposed{" "}
              {plural(result?.pendingCount ?? 0, "fill is", "fills are")} still awaiting review.
            </Notice>
          )}

          {(result?.rejectedCount ?? 0) > 0 && (
            <Notice tone="destructive">
              {result?.rejectedCount} accepted{" "}
              {plural(result?.rejectedCount ?? 0, "fill", "fills")} couldn't be written and{" "}
              {plural(result?.rejectedCount ?? 0, "needs", "need")} attention.
            </Notice>
          )}

          {recalcNotice && <Notice tone="warning">{recalcNotice}</Notice>}

          {isXlsm && <Notice tone="muted">{MACROS_NOTICE}</Notice>}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          {result && (
            <a
              href={result.downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(buttonVariants({ variant: "default" }))}
            >
              <Download className="mr-1.5 h-4 w-4" />
              Download workbook
            </a>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function Notice({
  tone,
  children,
}: {
  tone: "warning" | "destructive" | "muted"
  children: React.ReactNode
}) {
  return (
    <p
      className={cn(
        "flex items-start gap-1.5 text-xs",
        tone === "warning" && "text-[hsl(var(--status-warning))]",
        tone === "destructive" && "text-destructive",
        tone === "muted" && "text-muted-foreground"
      )}
    >
      {tone !== "muted" && <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />}
      <span>{children}</span>
    </p>
  )
}

interface ExportHistoryDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  exports: Array<{ id: string; createdAt: string; downloadUrl: string; appliedProposalIds: string[] }>
}

function ExportHistoryDialog({ open, onOpenChange, exports }: ExportHistoryDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Export history</DialogTitle>
          <DialogDescription>
            Every filled workbook produced from this schedule. Links stay valid for a short while.
          </DialogDescription>
        </DialogHeader>

        {exports.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No exports yet — accept some fills, then apply them.
          </p>
        ) : (
          <div className="max-h-80 divide-y divide-border overflow-auto">
            {exports.map((item) => (
              <div key={item.id} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-sm">{formatTimestamp(item.createdAt)}</p>
                  <p className="text-meta">
                    {item.appliedProposalIds.length}{" "}
                    {plural(item.appliedProposalIds.length, "fill", "fills")} applied
                  </p>
                </div>
                <a
                  href={item.downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(buttonVariants({ variant: "ghost", size: "sm" }))}
                >
                  Download
                </a>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
